import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import domtoimage from 'dom-to-image';
import sizeUtil from "../util/Util.js";

const SimulationWrap = styled.div`
    width:100%;
    height:100%;
    overflow:hidden;
    transform-origin: center;
`;

const PieceStyle = styled.div`
    position: relative;
    width:${props => props.w}px;
    height:${props => props.h}px;
    &::before{
        position:absolute;
        content:"";
        width:${p => 100 * p.scale}%;
        height: ${p => 100 * p.scale}%;
        opacity: 0.5;
        left:0;
        right:0;
        background-color: transparent;
        border:${props => props.border}px solid #000;
        mix-blend-mode: overlay;
        box-sizing:border-box;
        transform:scale(${p => 1 / (p.scale)});
        transform-origin: left top;
        z-index:2;
    }
    &::after{
        position:absolute;
        content:"";
        width:calc(${p => 100 * p.scale}% - 2px);
        height: calc(${p => 100 * p.scale}% - 2px);
        left:1px;
        top:1px;
        opacity: .2;
        mix-blend-mode:overlay;
        border:${props => props.border}px solid #fff;
        box-sizing:border-box;
        transform-origin: left top;
        transform:scale(${p => 1 / (p.scale)});
        z-index:2;
    }
    .pieceBackground{
        width:${props => props.w}px;
        height:${props => props.h}px;
        z-index:-1;
        background-image: url(${props => props.fileUrl});
        background-size: 300px;
        transform:scale(1);
    }
`;

const OffsetTypeWrap = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: flex-start;
    overflow:hidden;
    & .piece{
        flex-shrink:0;
        flex-grow:0;
        position: relative;
        &:nth-of-type(1n){
        }
        &:nth-of-type(2n){
        }
        &:nth-of-type(3n){
        }
    }
`;

const Col = styled.div`

`;

const Piece = React.memo(function ({ size, containerSize, fileUrl, style, border = 1 }) {
    return (
        <PieceStyle className="piece"
            w={size.w} h={size.h}
            containerSize={containerSize}
            border={border}
            scale={size.scale}
            fileUrl={fileUrl}
            style={{
                ...style,
            }}
        >
            <div className="pieceBackground"
                style={{
                    backgroundPositionX: window.crypto.getRandomValues(new Uint8Array(1))[0] + "%",
                    backgroundPositionY: window.crypto.getRandomValues(new Uint8Array(1))[0] + "%"
                }}></div>
        </PieceStyle >
    )
});

function OffsetType({ size, containerSize, fileUrl, offsetPercent, offestStep }) {
    const colCount = Math.ceil(containerSize.width / size.w);
    const cols = [];
    for (let c = 0, len = colCount; c < len; c++) {
        let offset = c % offestStep;
        cols.push(
            <Col size={size} key={c}>
                {(() => {
                    let rows = [];
                    let rowCount = Math.ceil((containerSize.height + (size.h * ((offsetPercent / 100 * offset)))) / size.h);
                    for (let r = 0, len = rowCount; r < len; r++) {
                        rows.push(<Piece key={r} size={size} containerSize={containerSize} fileUrl={fileUrl} style={{ transform: `translateY(${-offsetPercent * offset}%)` }}></Piece>);
                    }
                    return rows;
                })()}
            </Col>
        );
    }
    return (
        <OffsetTypeWrap>
            {cols}
        </OffsetTypeWrap>
    )
}

function HerringbonePiece({ size, containerSize, fileUrl, style }) {
    const pythagoras = Math.floor(Math.sqrt((size.w * size.w) + (size.w * size.w)));
    const gap = Math.floor((size.h - size.w) * Math.sin((45 * Math.PI / 180)));
    const longGap = Math.floor((size.h) * Math.sin((45 * Math.PI / 180)));
    const topStart = -gap;
    let top = pythagoras;
    const col = [];
    const colCount = Math.ceil(((containerSize.height + gap) / pythagoras));
    const rowCount = Math.ceil(((containerSize.width + (longGap)) / (longGap * 2)));
    for (let r = 0, rlen = rowCount; r < rlen; r++) {
        for (let c = 0, clen = colCount; c < clen; c++) {
            let currentTop = topStart + top * c;
            col.push(<>
                <Piece key={`k_${r}_${c}_1`} size={size} containerSize={containerSize} fileUrl={fileUrl} style={{ position: 'absolute', left: (longGap * 2) * r, top: currentTop, transform: "rotate(45deg)", transformOrigin: "left top" }}></Piece>
                <Piece key={`k_${r}_${c}_2`} size={size} containerSize={containerSize} fileUrl={fileUrl} style={{ position: 'absolute', left: (longGap * 2) * r, top: currentTop, transform: "rotate(-45deg)", transformOrigin: "left top" }}></Piece>
            </>);
        }
    }
    return (<>
        {col}
    </>);
}

function HerringboneType({ size, containerSize, fileUrl, offsetPercent, offestStep }) {
    const colCount = Math.ceil(containerSize.width / size.w);
    return (
        <OffsetTypeWrap>
            <HerringbonePiece size={size} containerSize={containerSize} fileUrl={fileUrl}></HerringbonePiece>
        </OffsetTypeWrap>
    )
}

const SpaceOverlay = styled.div`
    position: absolute;
    left:0;
    top:0;
    bottom:0;
    right:0;
    z-index:4;
    display: flex;
    mix-blend-mode: unset;
    &:nth-of-type(2){
        mix-blend-mode: soft-light;
        z-index:2;
    }
    &:nth-of-type(3){
        mix-blend-mode: multiply;
        z-index:3;
    }
    div{
        position: relative;
    }
    div:first-of-type,
    div:last-of-type{
        background-color: #e8e8e8;
        flex-grow: 1;
    }
    img.space{
        height: 100%;
        position: relative;
        z-index:2;
    }
    img.overlay{
        display:block;
        flex-shrink: 0;
        width:100%;
        position:absolute;
        z-index:1;
    }
    & + .simulation-wrap{
        transform:${props => {
        return sizeUtil(props.space).transform;
    }};
        height:${props => {
        return sizeUtil(props.space).height;
    }};
        width:${props => {
        return sizeUtil(props.space).width;
    }};
    }
`;

const SimulationContainer = styled.div`
    width:100%;
    height:100%;
    perspective:${p => sizeUtil(p.space).perspective}px;
    perspective-origin:${p => sizeUtil(p.space).perspectiveOrigin};
    display: flex;
    justify-content: center;
    align-items: center;
    overflow:hidden;
    position:relative;
    flex-grow:1;
    background-color: #e8e8e8;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: 'Noto Sans KR', sans-serif;
    font-size: 28px;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.43;
    letter-spacing: -0.7px;
    text-align: center;
    color: #a2a2a2;
    flex-direction: column;
    
`;

const SwapBtnContainer = styled.div`
    position:absolute;
    bottom:50px;
    left:50px;
    z-index:99;
    display:flex;
`;

const SwapBtn = styled.button`
    background-image: url(${(p) => {
        switch (p.bg) {
            case "sigong":
                return "comp-img/tab_design-1.png";
            case "space":
                return "comp-img/tab_space-1.png";
        }
    }});
    margin-right:24px;
    display:flex;
    align-items:flex-end;
    width:45px;
    height:75px;
    justify-content:center;
    background-repeat:no-repeat;
    background-position-x:center;
    background-position-y:2.5px;
    background-size:40px 40px;
    background-color:unset;
    border:0;
    font-family: 'Noto Sans KR', sans-serif;
    font-size: 15px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.67;
    letter-spacing: normal;
    text-align: left;
    color: #494949;
    cursor: pointer;
    &.checked{
        color:#a72b2a;
        font-weight: bold;
        background-size:45px 46px;
        background-position-y:0;
        background-image: url(${(p) => {
        switch (p.bg) {
            case "sigong":
                return "comp-img/tab_design-2.png";
            case "space":
                return "comp-img/tab_space-2.png";
        }
    }});
    }
`;

const UtilBtnContainer = styled.div`
    position:absolute;
    right:50px;
    bottom:50px;
    padding:0;
    display:flex;
    z-index:99;
    button{
        min-width:0;
        width:auto;
        display:inline;
        margin:0;
        padding: 0;
        background:unset;
        border:0;
        width:50px;
        cursor: pointer;
        img{
            width:100%;
            height:auto;
        }
    }
`;

const LoadingPage = styled.div`
    position:fixed;
    left:0;
    top:0;
    bottom:0;
    right:0;
    background-color: rgba(0,0,0,.5);
    z-index:999;
    color:#fff;
    display:none;
    align-items: center;
    justify-content: center;
`;

const Simulation = React.memo(({ space, fileUrl, patternName, size }) => {
    const sw = useRef(null);
    const swapBtn = useRef(null);
    const utilBtn = useRef(null);
    const loadingP = useRef(null);
    const [containerSize, setContainerSize] = useState(false);
    const [spaceSwap, setSpaceSwap] = useState(true);
    useEffect(() => {
        if (space === null) {
            setSpaceSwap(false);
        } else {
            setSpaceSwap(true);
        }
    }, [space, size, patternName, sw]);

    useEffect(() => {
        setContainerSize({
            width: sw.current.offsetWidth,
            height: sw.current.offsetHeight
        });
    }, [spaceSwap, space, size, patternName, sw]);

    const pieceSize = useCallback(() => {
        const ratio = sizeUtil(spaceSwap ? space : null).ratio;
        const scale = sizeUtil(spaceSwap ? space : null).scale;
        const count = Math.floor(containerSize.width / (size.split("X")[0] * ratio));
        const w = containerSize.width / count;
        const h = (size.split("X")[1] / size.split("X")[0]) * w;
        const pieceSize = { w: w, h: h, r: ratio, scale: scale };
        return pieceSize;
    }, [size, space, patternName, containerSize, spaceSwap, sizeUtil, fileUrl, sw]);

    const icDown = useCallback(() => {
        if (!window.confirm("공간 이미지를 다운 받는 경우, 1분 이상의 시간이 소요되며, 이미지 처리 중 브라우저가 멈출 수 있습니다.\n계속하시겠습니까?")) {
            return;
        }
        swapBtn.current.style.display = 'none';
        utilBtn.current.style.display = 'none';
        domtoimage.toJpeg(document.querySelector('.left-side'), {
            quality: 0.95,
            width: 1480,
            height: 995
        }).then(function (dataUrl) {
            saveAs(dataUrl, 'simulation.png');
            swapBtn.current.style.display = 'flex';
            utilBtn.current.style.display = 'flex';
        }).catch((e) => {
            swapBtn.current.style.display = 'flex';
            utilBtn.current.style.display = 'flex';
        });

        function saveAs(uri, filename) {
            var link = document.createElement('a');
            if (typeof link.download === 'string') {
                link.href = uri;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                window.open(uri);
            }
        }
    }, [space]);

    return (
        <SimulationContainer space={space}>
            {spaceSwap && space ?
                <>
                    <SpaceOverlay space={spaceSwap ? space : null} cscale={pieceSize().scale}>
                        <div></div>
                        <div>
                            <img className="space" src={`space/${space}_interior.png`} />
                        </div>
                        <div></div>
                    </SpaceOverlay>
                    <SpaceOverlay space={spaceSwap ? space : null} cscale={pieceSize().scale}>
                        <div></div>
                        <div>
                            <img className="soft-light" src={`space/${space}_light.png`} />
                            <img style={{ opacity: 0 }} className="space" src={`space/${space}_interior.png`} />
                        </div>
                        <div></div>
                    </SpaceOverlay>
                    <SpaceOverlay space={spaceSwap ? space : null} cscale={pieceSize().scale}>
                        <div></div>
                        <div>
                            <img className="overlay" src={`space/${space}_dark.png`} />
                            <img style={{ opacity: 0 }} className="space" src={`space/${space}_interior.png`} />
                        </div>
                        <div></div>
                    </SpaceOverlay>
                </>
                : null}
            <SimulationWrap ref={sw} className="simulation-wrap">
                {(() => {
                    switch (patternName) {
                        case "plank1":
                            return <OffsetType size={pieceSize()} containerSize={containerSize} fileUrl={fileUrl} offsetPercent={25} offestStep={3}></OffsetType>;
                        case "plank2":
                            return <OffsetType size={pieceSize()} containerSize={containerSize} fileUrl={fileUrl} offsetPercent={50} offestStep={2}></OffsetType>;
                        case "herringbone":
                            return <HerringboneType size={pieceSize()} containerSize={containerSize} fileUrl={fileUrl} offsetPercent={50} offestStep={2}></HerringboneType>;
                        case "square1":
                            return <OffsetType size={pieceSize()} containerSize={containerSize} fileUrl={fileUrl} offsetPercent={0} offestStep={1}></OffsetType>;
                        case "square2":
                            return <OffsetType size={pieceSize()} containerSize={containerSize} fileUrl={fileUrl} offsetPercent={50} offestStep={2}></OffsetType>;
                        default:
                            return;
                    }
                })()}
            </SimulationWrap>
            <SwapBtnContainer ref={swapBtn}>
                <SwapBtn className={spaceSwap === false ? "checked" : null} bg="sigong" onClick={() => {
                    setSpaceSwap(false);
                }}>시공</SwapBtn>
                <SwapBtn className={spaceSwap === true ? "checked" : null} bg="space" onClick={() => {
                    if (space !== null) {
                        setSpaceSwap(true);
                    }
                }}>공간</SwapBtn>
            </SwapBtnContainer>
            <UtilBtnContainer ref={utilBtn}>
                <button onClick={icDown}>
                    <img src="comp-img/ic_down.png" alt="" />
                </button>
            </UtilBtnContainer>
            <LoadingPage ref={loadingP}>Loading...</LoadingPage>
        </SimulationContainer>
    );
});

export default Simulation;